const post = {
  slug: "instagram-dm-automation-glossary",
  title: "Instagram DM Automation Glossary: 30 Terms in Plain English",
  seoTitle: "Instagram DM Automation Glossary (30 Terms)",
  description:
    "Trigger, flow, private reply, webhook, handover: 30 Instagram DM automation terms explained in plain English, so you can read any tool’s pages with confidence.",
  excerpt:
    "Automation tools use a lot of jargon. This glossary explains 30 terms you will meet, grouped by how they fit together, with examples from selling on Instagram.",
  category: "Automation",
  tags: ["Instagram DM automation glossary", "DM automation terms", "Instagram automation for beginners"],
  date: "2026-11-06T12:00:00Z",
  cover: {
    src: "/blog-images/instagram-dm-automation-glossary-cover.jpg",
    alt: "A phone listing Instagram DM automation terms such as trigger, flow, handover and webhook",
  },
  blocks: [
    {
      type: "p",
      text:
        "Read any Instagram automation tool’s website and you will meet words like trigger, flow, handover and webhook. None of them is hard, but nobody explains them. This glossary does, in plain English, with examples from selling on Instagram. The terms are grouped by how they fit together, so you can read it straight through or jump to the part you need.",
    },
    {
      type: "p",
      text:
        "If you are new to the whole subject, start with [what Instagram DM automation is](/blog/what-is-instagram-dm-automation) and come back here when you meet a word you do not know.",
    },

    { type: "h2", text: "How a conversation starts" },
    {
      type: "table",
      headers: ["Term", "Plain-English meaning"],
      rows: [
        ["Trigger", "The event that starts an automation, such as a comment, a Story reply or a message."],
        ["Keyword", "A word or phrase that the tool watches for, such as LINK or a product name."],
        ["Comment-to-DM", "An automation that sends a private message when someone comments a keyword on your post or Reel."],
        ["Story reply", "A message a customer sends by replying to your Instagram Story. It can start an automated conversation."],
        ["Private reply", "The one direct message a business can send in response to a comment."],
        ["Message request", "Where a first message lands in the inbox of a person who does not follow you."],
      ],
    },
    {
      type: "p",
      text:
        "See [comment-to-DM](/features/instagram-comment-to-dm-automation) and [Story reply automation](/features/instagram-story-reply-automation) for how Checkout DM uses these.",
    },

    { type: "h2", text: "The rules from Meta" },
    {
      type: "table",
      headers: ["Term", "Plain-English meaning"],
      rows: [
        ["Official login (OAuth)", "Instagram’s own screen where you approve what a tool may do, so the tool never sees your password."],
        ["Professional account", "The type of Instagram account that business tools connect to."],
        ["Messaging window", "The time during which a tool may reply. In a DM conversation, replies must come within 24 hours of the customer’s last message."],
        ["Private reply window", "A comment can receive one private reply, within 7 days of the comment."],
        ["Permissions", "The list of things a tool asks Instagram to let it do, which you approve when connecting."],
        ["Revoking access", "Removing a tool’s connection from your Instagram settings, which you can do at any time."],
      ],
    },
    {
      type: "p",
      text:
        "These come from Meta’s published rules. Read [whether Instagram DM automation is safe](/blog/is-instagram-dm-automation-safe-2026) for the full list of limits.",
    },

    { type: "h2", text: "Running the conversation" },
    {
      type: "table",
      headers: ["Term", "Plain-English meaning"],
      rows: [
        ["Flow", "A multi-step conversation with a path: ask, wait for an answer, move on."],
        ["Automation rule", "One trigger with one action, such as “when someone comments LINK, send this product”."],
        ["Bot", "The automated side of a conversation."],
        ["Chat state", "Where the bot is in a conversation, such as waiting for a size or for payment."],
        ["Handover", "Passing a conversation from the bot to a person."],
        ["Pause Bot", "A control that stops the bot in one chat so you can talk without it replying over you."],
        ["Stuck chat", "A conversation that needs a person, for example when the bot could not understand a reply."],
        ["Bot loop", "A runaway pattern of messages going back and forth, often between automated accounts. Checkout DM stops replying to a customer for good when it detects an unusually high volume of messages in a short time."],
      ],
    },

    { type: "h2", text: "Selling and payments" },
    {
      type: "table",
      headers: ["Term", "Plain-English meaning"],
      rows: [
        ["Catalog", "Your list of products with photos, prices and sizes."],
        ["Variant", "A version of a product, such as a size or colour."],
        ["Checkout link", "A link where the customer completes the order."],
        ["Payment gateway", "A service such as Razorpay, Stripe or PayPal that takes the payment and confirms it automatically."],
        ["Manual payment", "Paying by UPI, QR code or bank transfer, where you confirm the money arrived before shipping."],
        ["Abandoned cart", "A cart where the customer started an order but did not finish it."],
        ["RTO (return to origin)", "A parcel that comes back to you undelivered."],
      ],
    },
    {
      type: "p",
      text:
        "Our guide to [UPI versus a payment gateway](/blog/accept-payments-instagram-upi-vs-gateway) explains which payment method suits you, and [abandoned carts in Instagram DMs](/blog/abandoned-carts-instagram-dms) covers follow-up.",
    },

    { type: "h2", text: "Connecting tools together" },
    {
      type: "table",
      headers: ["Term", "Plain-English meaning"],
      rows: [
        ["Webhook", "A message one system sends another when something happens, such as a new order sent to your own software."],
        ["Integration", "A connection between two tools so they share information."],
        ["Dashboard", "The screen where you see orders, chats and settings."],
      ],
    },

    { type: "h2", text: "How the terms fit together" },
    {
      type: "p",
      text:
        "Here is one example. A customer comments the keyword LINK on your Reel. That comment is the trigger. The tool sends one private reply, inside the 7-day window, with the product. The customer answers, which opens the 24-hour messaging window, and the flow takes them through size, cart and address. They pay through a checkout link, and the order appears in your dashboard. If they ask for a person, the chat is handed over to you. Every term above appears in that single path.",
    },
    {
      type: "p",
      text:
        "You can see this path on the [Instagram DM automation page](/features/instagram-dm-automation).",
    },
  ],
  related: [
    { to: "/features/instagram-dm-automation", title: "Instagram DM automation", description: "See how these terms appear in a real checkout flow." },
    { to: "/features", title: "All Checkout DM features", description: "From first message to shipped order." },
    { to: "/faq", title: "Frequently asked questions", description: "Answers on payments, shipping and returns." },
  ],
  faqs: [
    {
      q: "What is a trigger in Instagram automation?",
      a: "A trigger is the event that starts an automation, such as a comment with a keyword, a Story reply or a direct message.",
    },
    {
      q: "What is a private reply?",
      a: "A private reply is the one direct message a business can send in response to a comment. Under Meta’s rules, it must be sent within 7 days of the comment.",
    },
    {
      q: "What does handover mean?",
      a: "Handover means passing a conversation from the automated bot to a person, for example when a customer asks to speak to someone or the bot cannot understand a reply.",
    },
    {
      q: "What is a webhook?",
      a: "A webhook is an automatic message one system sends to another when something happens. For example, Checkout DM Pro can push order and return events to your own system as they occur.",
    },
    {
      q: "What does OAuth mean?",
      a: "OAuth is the official login method. You approve a tool’s permissions on Instagram’s own screen, and the tool never sees your password.",
    },
  ],
};

export default post;
